import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, startWith, map } from 'rxjs';
import { COUNTRIES, Country, defaultCountry } from '../../utils/countries';

@Component({
  selector: 'app-company-register',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    CommonModule,
    MatButtonModule,
    TranslateModule,
  ],
  templateUrl: './company-register.component.html',
  styleUrl: './company-register.component.scss'
})
export class CompanyRegisterComponent implements OnInit {
  registerForm: FormGroup;
  errorMessage: string = '';
  isLoading: boolean = false;
  isFromExtension: boolean = false;
  redirect_to_buy: boolean = false;

  countries: Country[] = COUNTRIES;
  filteredCountries$!: Observable<Country[]>;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) {
    const initial = defaultCountry();

    this.registerForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      country: [initial, [Validators.required, this.validCountry()]],
      phone: ['', [Validators.required, Validators.pattern(/^[\d\s-]{6,20}$/)]],
      terms: [false, Validators.requiredTrue],
      linkedin_url: [''],
    });

    this.route.queryParams.subscribe((params: any) => {
      if (params['redirect_to_buy']) {
        this.redirect_to_buy = true;
      }
    });
  }

  ngOnInit() {
    this.filteredCountries$ = this.registerForm.get('country')!.valueChanges.pipe(
      startWith(this.registerForm.get('country')!.value),
      map(v => this.filterCountries(v)),
    );
  }

  displayCountry = (c: Country | string | null): string => {
    if (!c) return '';
    if (typeof c === 'string') return c;
    return c.dialCode;
  };

  trackByCode = (_: number, c: Country) => c.code;

  onCountrySelected(c: Country) {
    this.registerForm.get('country')!.setValue(c, { emitEvent: false });
  }

  countryName(c: Country): string {
    const lang = this.translate.currentLang || this.translate.getDefaultLang();
    return lang === 'es' ? c.nameEs : c.name;
  }

  private filterCountries(value: Country | string | null): Country[] {
    if (!value) return this.countries;
    if (typeof value === 'object') return this.countries;

    const q = value.toString().toLowerCase().trim().replace(/\s+/g, '');
    if (!q) return this.countries;

    const isEs = (this.translate.currentLang || this.translate.getDefaultLang()) === 'es';

    return this.countries.filter(c => {
      const localName = (isEs ? c.nameEs : c.name).toLowerCase();
      const altName = (isEs ? c.name : c.nameEs).toLowerCase();
      return (
        localName.includes(q) ||
        altName.includes(q) ||
        c.code.includes(q) ||
        c.dialCode.toLowerCase().includes(q) ||
        c.dialCode.replace('+', '').includes(q.replace('+', ''))
      );
    });
  }

  private validCountry() {
    return (control: any) => {
      const v = control.value;
      if (v && typeof v === 'object' && 'dialCode' in v) return null;
      return { invalidCountry: true };
    };
  }

  register() {
    if (this.registerForm.valid) {
      this.isLoading = true;

      const v = this.registerForm.value;
      const country: Country = v.country;
      const phoneDigits = (v.phone ?? '').toString().replace(/\D/g, '');

      const payload: any = {
        name: v.name,
        email: v.email,
        password: v.password,
        phone: `${country.dialCode}${phoneDigits}`,
        terms: v.terms,
        linkedin_url: v.linkedin_url,
        redirect_to_buy: this.redirect_to_buy ? 1 : 0,
      };

      this.http.post(`${environment.url}/api/company/register`, payload).subscribe({
        next: (response: any) => {
          console.log(response);
          this.isLoading = false;
          this.router.navigate(['/company/validate-email'], {
            queryParams: {
              email: this.registerForm.get('email')?.value,
              redirect_to_buy: this.redirect_to_buy ? 1 : 0,
            },
          });
        },
        error: (error: any) => {
          console.error('Error:', error);
          this.isLoading = false;
          if (error.status === 400) {
            if (error.error && error.error.code === 'EMAIL_ALREADY_EXISTS') {
              this.errorMessage = this.translate.instant('companyRegister.errors.emailExists');
            } else if (error.error && error.error.code === 'MISSING_INFO') {
              this.errorMessage = this.translate.instant('companyRegister.errors.missingInfo');
            } else {
              this.errorMessage = this.translate.instant('companyRegister.errors.unknownError');
            }
          }
        },
      });
    } else {
      this.markAllAsTouched();
      this.errorMessage = this.registerForm.invalid
        ? this.translate.instant('companyRegister.errors.incompleteFields')
        : '';
      console.error('Form is invalid');
    }
  }

  private markAllAsTouched() {
    Object.values(this.registerForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  @HostListener('document:click', ['$event'])
  handleClick(event: MouseEvent) {
    if (!(event.target as HTMLElement).closest('form')) {
      this.registerForm.markAsPristine();
      this.registerForm.markAsUntouched();
    }
  }
}
